import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { Portal } from '@codex/gadgets';
import { toggleOverflow } from '@codex/helpers';
import { useEscape } from '@codex/hooks';
import { Close } from '@codex/organisms';

const slideLeft = keyframes`
  0% {
    left: 430px;
  }
  100% {
    left: 0;
  }
`;

const showPopup = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: -8px;
  z-index: 1020;
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  overflow-y: auto;
  ${(props) => props.theme.fonts.default};
  width: 100%;
  height: 100%;
  animation: ${slideLeft} 0.5s ease;
  padding-top: 0;
`;

const Wrapper = styled.div`
  max-width: 600px;
  height: 100%;
  box-shadow: 1px 2px 4px 0 rgba(34, 34, 34, 0.3);
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.shades[7]};

  @media ${(props) => props.theme.breakpoints.tablet} {
    max-width: 500px;
  }

  @media ${(props) => props.theme.breakpoints.mobile} {
    margin-left: 40px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[2]} 8px ${theme.spacing[2]} ${theme.spacing[2]}`};
  border-radius: 4px 4px 0 0;
  color: ${(props) => props.theme.colors.shades[7]};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  font-size: 14px;
  background-color: ${(props) => props.theme.colors.primary.hover};
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1010;
  width: 100%;
  height: 100%;
  background-color: rgba(34, 34, 34, 0.6);
  animation: ${showPopup} 0.2s ease;
`;

const RightSidePopup = (props) => {
  const handleWrapperClick = useCallback((event) => event.stopPropagation(), []);

  useEscape(props.onClose);

  useEffect(() => {
    toggleOverflow('body', true);

    return () => toggleOverflow('body', false);
  }, []);

  return (
    <Portal domNode={document.body}>
      <Backdrop />
      <Overlay
        className={'dc-popup-overlay'}
        onClick={props.onClose}
        data-qa={props.qa}
      >
        <Wrapper onClick={handleWrapperClick}>
          <>
            {!props.hideHeaderBar && (
            <Header>
              <div>{props.title}</div>
              <Close onClose={props.onClose} />
            </Header>
            )}
            {props.children}
          </>
        </Wrapper>
      </Overlay>
    </Portal>
  );
};

RightSidePopup.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  onClose: PropTypes.func,
  hideHeaderBar: PropTypes.bool,
  qa: PropTypes.string
};

RightSidePopup.defaultProps = {
  title: '',
  onClose: undefined,
  hideHeaderBar: false,
  qa: undefined
};

export default RightSidePopup;
